import { Box, MenuItem, Skeleton, Typography } from "@mui/material";
import { Track } from "../../../types";
import MoreActionsMenu from "@/modules/common/components/ui-kit/menus/more-actions-menu";
import RentalTerms from "./rental-terms";
import FinancialDetails from "./financial-details";
import ListingEmptyState from "../common/empty-state";
import { useGetActiveListing } from "../../../hooks/listings.hook";
import EditRentalListingModal from "./rental-listing-forms/edit-rental-listing";
import PublishRentalListing from "./publish-rental-listing/publish-rental-listing";
import { Pencil, Trash2 } from "lucide-react";
import UnpublishListingModal from "../common/unpublish-listing/unpublish-listing-modal";
import { useState } from "react";
import CheckItem from "../common/check-item";
import { ListingType } from "@/modules/property-tracks/dtos";
import ListingMedia from "../common/listing-media";
import AddRentalListing from "./rental-listing-forms/add-rental-listing";
import FullPageModal from "@/modules/common/components/ui-kit/full-page-modal/full-page-modal";
import ListingStatusItem from "../common/status";

export default function RentalActiveListingMobileTab({
  open,
  onClose,
  track,
}: Readonly<{
  open: boolean;
  onClose: () => void;
  track: Track;
}>) {
  const [editRentalListingModalOpen, setEditRentalListingModalOpen] =
    useState(false);
  const [unpublishListingModalOpen, setUnpublishListingModalOpen] =
    useState(false);
  const { data, isLoading } = useGetActiveListing({
    params: {
      propertyUid: track.property.uid,
      listingType: ListingType.RENTAL,
    },
  });

  if (isLoading) return <Skeleton />;

  if (!data)
    return (
      <ListingEmptyState callToAction={<AddRentalListing track={track} />} />
    );

  return (
    <FullPageModal
      isOpen={open}
      title={
        <Box
          sx={(theme) => ({
            display: "flex",
            gap: theme.nawy.spacing.xs,
            flex: 1,
            alignItems: "center",
          })}
        >
          <Typography
            sx={(theme) => theme.nawy.typography.english.variants.headline.sm}
          >
            Listing details
          </Typography>
          <ListingStatusItem status={data.status} />
        </Box>
      }
      onClose={() => {
        onClose();
      }}
      extraButtons={
        data && (
          <>
            <PublishRentalListing listing={data} trackUid={track.uid} />
            <MoreActionsMenu>
              <MenuItem
                sx={(theme) => ({
                  display: "flex",
                  gap: theme.nawy.spacing.xs,
                })}
                onClick={() => setEditRentalListingModalOpen(true)}
              >
                <Pencil />
                Edit Listing
              </MenuItem>
              {data.inventoryListingId && (
                <MenuItem
                  sx={(theme) => ({
                    display: "flex",
                    gap: theme.nawy.spacing.xs,
                    color: theme.nawy.colors.semantic.text.extension.negative,
                  })}
                  onClick={() => setUnpublishListingModalOpen(true)}
                >
                  <Trash2 />
                  Remove Listing
                </MenuItem>
              )}
            </MoreActionsMenu>
          </>
        )
      }
    >
      <Box
        sx={(theme) => ({
          backgroundColor: theme.nawy.colors.semantic.layer.primary,
          p: theme.nawy.spacing.md,
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.lg,
          borderRadius: theme.nawy.radius.xl,
        })}
      >
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.sm,
          })}
        >
          <Typography
            sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
          >
            Is this property rented?
          </Typography>
        </Box>

        <CheckItem text="Rented" isTrue={data.isRented} />
      </Box>

      <RentalTerms listing={data} />
      <FinancialDetails listing={data} />
      <ListingMedia title="Listing Media" documents={data.trackDocuments} />
      <EditRentalListingModal
        listing={data}
        open={editRentalListingModalOpen}
        trackUid={track.uid}
        handleOpen={() => setEditRentalListingModalOpen(true)}
        handleClose={() => setEditRentalListingModalOpen(false)}
      />
      <UnpublishListingModal
        listingUid={data.uid}
        open={unpublishListingModalOpen}
        setOpen={setUnpublishListingModalOpen}
      />
    </FullPageModal>
  );
}
